import React from 'react';
import * as Icons from 'components/icons';
import * as Utils from 'utils';
import { FieldProps, FormValidation, IChangeEvent, ISubmitEvent } from '@rjsf/core';
import { DocumentStatusEnum } from 'trace-backend-sdk';
import { useIntl } from 'lib/intl';
import { JsonForm } from 'components/form-elements/JsonForms/components/JsonForm/JsonForm';
import { Box, Container } from 'components/layout';
import { MultiSelectAggregate } from 'components/form-elements/JsonForms/components/MultiSelectAggregate/MultiSelectAggregate';
import { OtherField } from 'components/form-elements/JsonForms/components/OtherField';
import { useDocumentParams } from 'pages';
import { DocumentsService, DocumentTemplateId } from 'services/documents';
import { InlineLoadingIndicator } from 'components/LoadingIndicator';
import { OutlinedTextWidget } from 'components/form-elements/JsonForms/components/Text';
import {
  DocumentTitleContent,
  DocumentTitleDate,
  DocumentTitleIconWrapper,
  DocumentTitleTemplateName,
  DocumentTitleWrapper,
} from 'components/DocumentTitleWrapper/DocumentTitleWrapper';
import { useLocaleDateFormat } from 'utils/hooks/use-locale-date-format';
import { generateContext } from 'utils/context';
import { PictureTake } from 'components/form-elements/JsonForms/components/PictureTake';
import { Button, Label } from 'components/form-elements';
import { Spacer } from 'components/Spacer/Spacer';
import {
  Temperature,
  TemperatureToggle,
} from 'components/TemperatureToggle/TemperatureToggle';
import { TimerText } from 'components/Timer/Timer';
import { useModal } from 'store/modals';
import { ConfirmationDialog } from 'components/confirmation-dialog/ConfirmationDialog';
import { Text } from 'components/typography';
import { ReliningFieldWrapper } from '../components/ReliningFieldWrapper';
import { ReliningImageWithTitle } from '../components/ReliningImageWithTitle';
import { ReliningBatch } from '../components/ReliningBatch';
import { ReliningCalculator } from '../components/ReliningCalculator';
import { DocumentSectionWrapper } from '../../document/components/DocumentSectionWrapper/DocumentSectionWrapper';
import { ReliningImage } from '../components/ReliningImage';
import { ReliningFileUpload } from '../components/ReliningFileUpload';
import { ReliningMachinePicture } from '../components/ReliningMachinePicture';
import { DeleteDocument } from '../../document/components/DeleteDocument/DeleteDocument';
import { ReliningTemperatureSelect } from '../components/ReliningTemperatureSelect';
import { celsiusToFahrenheit, fahrenheitToCelsius } from '../utils/temperature';
import { HardeningTimer } from '../components/HardeningTimer';
import { InstallationOtherWidget } from '../components/InstallationOtherWidget';
import { SprayLayers } from '../components/SprayLayers';
import {
  EquipmentTypeWidget,
  EQUIPMENT_TYPE_MAX_WORDS_ERROR,
  isEquipmentTypeOverLimit,
} from '../components/EquipmentTypeWidget';
import { UvHardeningSpeedWidget } from '../components/UvHardeningSpeedWidget';
import { GpsCoordinatesWidget } from '../components/GpsCoordinatesWidget';
import { uiSchema } from '../assets/ui-schema';
import { DocumentCertificateAccess } from '../../document/components/DocumentCertificateAccess/DocumentCertificateAccess';
import { ReliningSchemaFinal } from '../../../global/types/templates';
import { useLocationPermissionPrompt } from '../../location';

function mapDotSeparatedNumber(values: number[]) {
  return parseFloat(values.join('.'));
}

function InstallationPressure(props: FieldProps) {
  return (
    <MultiSelectAggregate
      {...props}
      onAggregateValues={mapDotSeparatedNumber}
      variant="gray"
      fieldSeparator={(
        <Box
          sx={{
            width: '1px',
            alignSelf: 'stretch',
            backgroundColor: '#3e3e53',
            isolation: 'isolate',
          }}
        />
      )}
    >
      {props.children}
    </MultiSelectAggregate>
  );
}

function HardeningTimeEstimate(props: FieldProps) {
  return (
    <MultiSelectAggregate
      {...props}
      backgroundLightness="light"
      variant="running"
      fieldSeparator={<TimerText sx={{ mb: 4 }}>:</TimerText>}
      containerSx={{ px: 10 }}
    />
  );
}

function Hardening(props: FieldProps) {
  return (
    <MultiSelectAggregate
      {...props}
      onAggregateValues={mapDotSeparatedNumber}
      variant="deepBlue"
      fieldSeparator={(
        <Box
          sx={{
            width: '1px',
            alignSelf: 'stretch',
            backgroundColor: '#5168a1',
            isolation: 'isolate',
          }}
        />
      )}
    >
      {props.children}
    </MultiSelectAggregate>
  );
}

function DoubleBorderField(props: FieldProps) {
  return (
    <ReliningFieldWrapper>
      <props.registry.fields.ObjectField {...props} />
    </ReliningFieldWrapper>
  );
}

/*
 * Document metadata fields, needed because it implements the
 * document title within its HTML element.
 * */
function Metadata(props: FieldProps) {
  const intl = useIntl();
  const { localeDateFormat } = useLocaleDateFormat();
  const { documentId, templateId, dateCreated, isCompleted } = useReliningFormContext();

  return (
    <DocumentSectionWrapper>
      <DocumentTitleWrapper>
        <DocumentTitleIconWrapper>
          <Icons.ReliningSplash />
        </DocumentTitleIconWrapper>

        <DocumentTitleContent>
          {dateCreated && (
            <DocumentTitleDate isDocumentCompleted={isCompleted}>
              {localeDateFormat(dateCreated)}
            </DocumentTitleDate>
          )}

          <DocumentTitleTemplateName>
            {intl.formatMessage({
              id: 'relining.templateName',
            })}
          </DocumentTitleTemplateName>
        </DocumentTitleContent>

        <Box
          sx={{
            display: 'inline-flex',
            ml: 'auto',
            alignSelf: 'baseline',
          }}
        >
          <DeleteDocument templateId={templateId} documentId={documentId} />
        </Box>
      </DocumentTitleWrapper>

      {isCompleted && (
        <React.Fragment>
          <DocumentCertificateAccess />
          <Spacer y={8} />
        </React.Fragment>
      )}

      <props.registry.fields.ObjectField {...props} />
    </DocumentSectionWrapper>
  );
}

function TemperatureInPipeImage(props: FieldProps) {
  const intl = useIntl();

  return (
    <React.Fragment>
      <Label
        variant="jsonInputLabel"
        labelIntlId="relining.temperatureInPipe"
      />

      <Spacer y={3} />

      <PictureTake
        {...props}
        title={intl.formatMessage({
          id: 'relining.temperatureInPipe',
        })}
        imageName="temperatureInPipe"
        retakeWarningIntlId="generic.confirm.areYouSure"
        TitleIcon={(
          <Box
            sx={{
              p: '6px',
              borderRadius: 'rounded',
              backgroundColor: 'grey.800',
            }}
          >
            <Icons.Thermostat color="white.50" />
          </Box>
        )}
        ActionButton={(
          <Button
            prependIcon={<Icons.CameraMui color="white.50" />}
            intlId="relining.batch.cameraButton"
          />
        )}
      />
    </React.Fragment>
  );
}
/*
 * Wraps the functionality of selecting temperature scales (F or C)
 * and selecting a temperature, using a custom widget.
 *
 * Implements some cross-widget interactions, such as converting
 * the temperature value when the temperature scale changes.
 * */
function HeatHardeningField(props: FieldProps) {
  function onTemperatureChange(temperature: number) {
    props.onChange({
      ...props.formData,
      temperature,
    });
  }

  function onTemperatureScaleChange(temperatureScale: Temperature) {
    /*
     * If the temperature scale changed *to* Celsius, then the current
     * temperature value is in Fahrenheit and we need to convert it
     * to Celsius. The same applies in the opposite case.
     *
     * So, if we had selected 40C and then changed the scale from C -> F,
     * then we expect the temperature value to change to F equivalent of 40C, which is 104F.
     * */
    const newTemperature = temperatureScale === 'celsius'
      ? fahrenheitToCelsius(props.formData.temperature)
      : celsiusToFahrenheit(props.formData.temperature);

    props.onChange({
      ...props.formData,
      temperature: newTemperature,
      temperatureScale,
    });
  }

  return (
    <React.Fragment>
      <ReliningTemperatureSelect
        disabled={props.disabled}
        value={props.formData.temperature}
        onChange={(newValue) => onTemperatureChange(newValue)}
        temperatureScale={props.formData.temperatureScale}
      />

      <Spacer y={8} />

      <TemperatureToggle
        disabled={props.disabled}
        value={props.formData.temperatureScale}
        onChange={(newValue) => onTemperatureScaleChange(newValue)}
      />
    </React.Fragment>
  );
}

const widgets = {
  other: OtherField,
  outlinedText: OutlinedTextWidget,
  installationOther: InstallationOtherWidget,
  equipmentType: EquipmentTypeWidget,
  uvHardeningSpeed: UvHardeningSpeedWidget,
  gpsCoordinates: GpsCoordinatesWidget,
};

const fields = {
  hardeningTimer: HardeningTimer,
  imageWithTitle: ReliningImageWithTitle,
  installationPressure: InstallationPressure,
  hardening: Hardening,
  batch: ReliningBatch,
  doubleBorderField: DoubleBorderField,
  calculator: ReliningCalculator,
  metadata: Metadata,
  reliningImage: ReliningImage,
  reliningFile: ReliningFileUpload,
  reliningMachine: ReliningMachinePicture,
  temperatureInPipeImage: TemperatureInPipeImage,
  heatHardening: HeatHardeningField,
  timeEstimate: HardeningTimeEstimate,
  sprayLayers: SprayLayers,
};

const [useReliningFormContext, ReliningFormContext] = generateContext<{
  documentId: string;
  templateId: DocumentTemplateId;
  dateCreated?: string;
  isCompleted: boolean;
  onTimerReset:(documentData: Record<string, any>) => void;
  onImageRemove: (documentData: Record<string, any>) => void;
}>();

/*
 * Fields that become irrelevant once "UV Hardening" is enabled on a relining
 * entry. They are hidden via the ui-schema dependents, and their data is
 * cleared here so it is never persisted through autosave nor shown on the
 * certificate (rather than waiting for the submit-time dependent stripping).
 * */
const UV_HARDENING_HIDDEN_FIELDS = [
  'temperatureInPipe',
  'hardeningTimeEstimate',
  'hardeningTime',
] as const;

function clearUvHardeningDependentFields(
  formData: ReliningSchemaFinal,
): ReliningSchemaFinal {
  const relinings = (formData as any)?.relinings;
  if (!Array.isArray(relinings)) {
    return formData;
  }

  let didChange = false;
  const nextRelinings = relinings.map((entry) => {
    if (!entry?.showUvHardening) {
      return entry;
    }

    const hasStaleData = UV_HARDENING_HIDDEN_FIELDS.some(
      (field) => entry[field] != null,
    );
    if (!hasStaleData) {
      return entry;
    }

    didChange = true;
    const nextEntry = { ...entry };
    UV_HARDENING_HIDDEN_FIELDS.forEach((field) => {
      delete nextEntry[field];
    });
    return nextEntry;
  });

  if (!didChange) {
    return formData;
  }

  return { ...formData, relinings: nextRelinings } as ReliningSchemaFinal;
}

export function ReliningPage() {
  const params = useDocumentParams();
  const { document, isLoading, hasDocumentWritePermission } = DocumentsService.useGetDocument(params.documentId, params.templateId);
  const { openModal } = useModal();

  const [formData, setFormData] = Utils.Document.useDocumentFormState() as unknown as [
      ReliningSchemaFinal,
      React.Dispatch<React.SetStateAction<ReliningSchemaFinal>>
    ];
  const onSubmitDocument = Utils.Document.useSubmitDocument();
  const onUpdateDocument = Utils.Document.useUpdateDocumentData();

  const onChange = React.useCallback(
    (e: IChangeEvent) => {
      const nextFormData = clearUvHardeningDependentFields(e.formData);
      setFormData(nextFormData);
      onUpdateDocument(nextFormData);
    },
    [onUpdateDocument, setFormData],
  );

  const onSubmit = React.useCallback(
    (e: ISubmitEvent<any>) => {
      onSubmitDocument(e.formData);
    },
    [onSubmitDocument],
  );

  const isCompletedDocument = document?.status === DocumentStatusEnum.Completed;
  const isFormEditable = hasDocumentWritePermission && !isCompletedDocument;

  /*
   * Keep a ref to the latest form data so the (async) GPS pre-fill can read the
   * current value without going stale or being tied into the effect deps.
   * */
  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;

  /*
   * Fills the optional GPS Coordinates header field, but never overwrites a
   * value the user already entered. Used both by the permission pop-up shown on
   * entering the form and by the previously-opted-in flow.
   * */
  const fillGpsCoordinates = React.useCallback(
    (gpsCoordinates: string) => {
      const currentFormData = formDataRef.current;
      if (currentFormData?.metadata?.gpsCoordinates) {
        return;
      }

      const newFormData = {
        ...currentFormData,
        metadata: {
          ...currentFormData?.metadata,
          gpsCoordinates,
        },
      } as ReliningSchemaFinal;

      setFormData(newFormData);
      onUpdateDocument(newFormData);
    },
    [onUpdateDocument, setFormData],
  );

  /*
   * On entering the Relining form, prompt (once) for device-location access and
   * pre-fill the GPS Coordinates field. Runs only after the document and its
   * template have loaded so it can't race the initial fetch. The field can
   * always be cleared and re-added manually via its own button, so this never
   * fights the user. Fully optional / backward compatible.
   * */
  useLocationPermissionPrompt({
    shouldRun: Boolean(document) && isFormEditable,
    onCoordinates: fillGpsCoordinates,
  });

  /*
   * The "installationOther" text field is required only when the
   * installation type is "other". For the predefined types it acts
   * as an optional additional subtitle.
   * */
  const validate = React.useCallback(
    (data: ReliningSchemaFinal, errors: FormValidation) => {
      data?.relinings?.forEach((entry, index) => {
        if (entry?.installation === 'other' && !entry?.installationOther?.trim()) {
          (errors as any)?.relinings?.[index]?.installationOther?.addError(
            'errors.installationOther.required',
          );
        }

        /*
         * The UV Hardening speed is only relevant when the toggle is on.
         * It must be a number between 0 and 100 with at most one decimal place.
         * */
        if (entry?.showUvHardening && entry?.uvHardeningSpeed != null) {
          const speed = entry.uvHardeningSpeed;
          const decimals = String(speed).split('.')[1]?.length ?? 0;

          if (Number.isNaN(speed) || speed < 0 || speed > 100) {
            (errors as any)?.relinings?.[index]?.uvHardeningSpeed?.addError(
              'errors.uvHardeningSpeed.range',
            );
          } else if (decimals > 1) {
            (errors as any)?.relinings?.[index]?.uvHardeningSpeed?.addError(
              'errors.uvHardeningSpeed.decimal',
            );
          }
        }

        /*
         * "Type of Equipment Used" is limited to a maximum number of words so
         * the value stays readable on the certificate and does not overlap
         * other sections. The widget shows this same error live while typing;
         * here we also block submission when the limit is exceeded.
         * */
        if (isEquipmentTypeOverLimit(entry?.preparations?.equipmentType)) {
          (errors as any)?.relinings?.[index]?.preparations?.equipmentType?.addError(
            EQUIPMENT_TYPE_MAX_WORDS_ERROR,
          );
        }
      });

      return errors;
    },
    [],
  );

  const formContext = React.useMemo(
    () => ({
      documentId: params.documentId,
      templateId: params.templateId,
      dateCreated: document?.dateCreated,
      isCompleted: document?.status === DocumentStatusEnum.Completed,
      /*
       * Exposed so widgets can read sibling values (e.g. the
       * installationOther widget adapts its label to the selected
       * installation type).
       * */
      formData,
      onTimerReset: (documentData: Record<string, any>) => {
        setFormData(documentData as any);
      },
      onImageRemove: (documentData: Record<string, any>) => {
        setFormData(documentData as any);
      },
    }),
    [
      document?.dateCreated,
      document?.status,
      params.documentId,
      params.templateId,
      formData,
      setFormData,
    ],
  );

  const handleAddRelining = React.useCallback(() => {
    const currentRelinings = formData?.relinings || [];
    const newFormData = {
      ...formData,
      relinings: [...currentRelinings, {}],
    };
    setFormData(newFormData as ReliningSchemaFinal);
    onUpdateDocument(newFormData);
  }, [formData, setFormData, onUpdateDocument]);

  const formRef = React.useRef<any>(null);

  const handleFinishDocument = React.useCallback(() => {
    openModal({
      modalType: 'componentModal',
      modalProps: {
        children: (
          <ConfirmationDialog
            onProceed={() => {
              if (formRef.current) {
                formRef.current.click();
              }
            }}
          >
            <Text
              as="p"
              sx={{ textAlign: 'center' }}
              intlId="relining.finishDocument.confirm"
            />
          </ConfirmationDialog>
        ),
      },
    });
  }, [openModal]);

  if (isLoading) {
    return <InlineLoadingIndicator />;
  }

  const isCompleted = document?.status === DocumentStatusEnum.Completed;
  const isDisabled = !hasDocumentWritePermission || isCompleted;

  return (
    <Container variant="spacer" sx={{ pt: 0 }}>
      <ReliningFormContext value={formContext}>
        <JsonForm
          formContext={formContext}
          disabled={isDisabled}
          widgets={widgets}
          fields={fields}
          formData={formData}
          onChange={onChange}
          onSubmit={onSubmit}
          name="relining"
          validate={validate}
          schema={document?.template?.templateSchemaFinal ?? {}}
          uiSchema={JSON.parse(JSON.stringify(uiSchema))}
        >
          {!isDisabled && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 4 }}
            >
              <Button
                variant="secondary"
                intlId="relining.addNewRelining"
                onClick={handleAddRelining}
                type="button"
              />
              <Button
                intlId="relining.finishDocument"
                onClick={handleFinishDocument}
                type="button"
              />
              <button
                ref={formRef} type="submit"
                style={{ display: 'none' }}
              />
            </Box>
          )}
        </JsonForm>
      </ReliningFormContext>
    </Container>
  );
}

export { useReliningFormContext };
